import { CloseOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import BookmarkIcon from '@/assets/icons/BookmarkIcon';
import { CenterFlex, Flex, ScrollVFlex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Modal from '@/components/Modal/Modal';
import { Stage } from '@/components/Stage/Stage';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import queryKeys from '@/configs/react-query-keys';
import AskSynclyFloat from '@/features/AskSyncly/components/AskSynclyFloat/AskSynclyFloat';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import {
  SearchDetailProvider,
  useSearchDetailContext,
} from '@/features/GlobalSearch/context/SearchDetailContext';
import {
  GLOBAL_SEARCH_VIEW,
  SEARCH_RESULT_STATUS,
} from '@/features/GlobalSearch/types/searchConst';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import InlineKeywordFilterV2 from '@/features/StructuredFilter/components/InlineKeywordFilterV2';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import usePolling from '@/hooks/pure/usePolling/usePolling';
import service from '@/services/index';
import useGlobalSearchStore from '@/stores/GlobalSearchStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { hiddenScrollbar } from '@/styles/style';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { GPT_DOMAIN } from '@/types/gptConst';
import { MAX_SAVED_TOPIC_COUNT } from '@/types/savedTopicConst';
import { track } from '@/utils/track/track';
import { isEmptyString } from '@/utils/util';

import useGlobalSearchModalAction from '../../hooks/useGlobalSearchModalAction';
import SearchFeedbackList from '../ResultContents/FeedbackTab/SearchFeedbackList';
import { ContentBox } from '../ResultContents/SearchContentStyles';
import SearchDateFilter from '../SearchDateFilter';
import searchQueryUtils from '../SearchQueryBuilder/searchQueryUtils';
import SingleKeywordFilter from '../SingleKeywordFilter';
import SaveAsTopicModal from './SaveAsTopicModal';
import SearchTab from './SearchTab';
import SearchTabRender from './SearchTabRender';
import SearchTitle from './SearchTitle';

type Props = {
  alias: string;
};

const SearchDetail = (props: Props) => {
  const appInit = useAppInit();
  const { alias } = props;
  const setSearchStoreState = useGlobalSearchStore(
    (state) => state.setSearchStoreState,
  );
  const searchResult = useGlobalSearchStore((state) => state.searchResult);

  const { data } = useQuery({
    queryKey: [...queryKeys.search.SEARCH_DETAIL, alias],
    queryFn: async () => {
      if (!alias) {
        return;
      }
      return await service.globalSearch.fetchSearchResult(alias);
    },
    enabled: appInit,
  });

  // useQuery 결과를 스토어에 동기화
   
  useEffect(() => {
    if (data) {
      setSearchStoreState({
        searchResult: data,
      });

      // Track search complete event
      const hasFilter = data.filter && Object.keys(data.filter).length > 0;

      track(AMP_KEYS.SEARCH_COMPLETE, {
        search_alias: data.alias,
        has_filter: hasFilter,
      });
    }
  }, [data]);

  if (!alias) {
    return null;
  }

  // alias 가드: 스토어의 searchResult가 현재 alias와 일치할 때만 렌더링
  // → toDetailPage()에서 searchResult: null 설정 → 이전 결과 잔존 방지
  const isReady = !!searchResult && searchResult.alias === alias;

  return (
    <Transition
      status={isReady}
      style={{ width: '100%', height: '100%' }}
      fallback={
        <CenterFlex height="100%">
          <Spinner />
        </CenterFlex>
      }
    >
      {searchResult && searchResult.alias === alias && (
        <SearchDetailProvider
          key={searchResult.alias}
          searchResult={searchResult}
        >
          <Content />
        </SearchDetailProvider>
      )}
    </Transition>
  );
};

const Content = () => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const addToast = useToast();
  const setPopup = usePopup();
  const wr = useWorkspaceRouter();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { copyLink, closeModal } = useGlobalSearchModalAction();
  const queryClient = useQueryClient();

  const setSearchStoreState = useGlobalSearchStore(
    (state) => state.setSearchStoreState,
  );
  const addToNotifyList = useGlobalSearchStore(
    (state) => state.addToNotifyList,
  );

  const [selectedTab, setSelectedTab] = useState<GLOBAL_SEARCH_VIEW>(
    GLOBAL_SEARCH_VIEW.FEEDBACK,
  );

  const {
    clearFilter,
    columns,
    filterChanged,

    frequency,
    aiKeywordFilter,
    dateRange,
    keywordFilter,
    filterQueryFormats,

    updateDataFilter,
    updateDateFilter,
    updateKeywordFilter,
    updateAiKeywordFilter,
    updateFrequency,

    updateOriginalFilterQueryFormats,
    updateOriginalDateRange,
    updateOriginalKeywordFilter,
    updateOriginalAIKeywordFilter,
    isFilterEmpty,
    searchResult,
  } = useSearchDetailContext();

  const createModalStatus = useModalState();

  const updateSearchResult = async () => {
    if (!filterChanged || isFilterEmpty) {
      return;
    }

    let newFilterQueryFormats = [...filterQueryFormats];

    if (keywordFilter) {
      newFilterQueryFormats.push({
        columnKey: FEEDBACK_COLUMNS.SNIPPET,
        operator: keywordFilter.op,
        filterValues: keywordFilter.values.map((v) => {
          return {
            filterKey: v,
            filterName: v.toString(),
          };
        }),
      });
    }

    const entityGroupFilter: StructuredFilterStringNumberValueSet | undefined =
      new SFilter(searchResult.filter).getSinlgeDataFilter(
        FEEDBACK_COLUMNS.ENTITY_GROUP,
      )?.[0];

    if (entityGroupFilter) {
      newFilterQueryFormats.push({
        columnKey: FEEDBACK_COLUMNS.ENTITY_GROUP,
        operator: entityGroupFilter.op,
        filterValues: entityGroupFilter.values.map((v) => {
          return {
            filterKey: v,
            filterName: v.toString(),
          };
        }),
      });
    }

    const structuredFilter = structuredFilterUtils.buildStructuredFilter(
      newFilterQueryFormats,
    );

    const payload: StructuredFilter = {
      ...structuredFilter,
      start_date: dateRange[0].toJSON(),
      end_date: dateRange[1].toJSON(),
    };

    let title: string;

    try {
      const newTitle = searchQueryUtils.getSearchTitle(
        aiKeywordFilter,
        newFilterQueryFormats,
        (key) => {
          const col = structuredFilterUtils.getFieldInfo(key, columns);
          const fieldInfo = col?.fieldInfo;
          return fieldInfo?.columnName || key;
        },
      );
      title = searchResult.title === newTitle ? `${newTitle} copy` : newTitle;
    } catch (e) {
      title = `${searchResult.title} copy`;
      console.error('[Error] getSearchTitle : invalid filter list', e);
    }

    try {
      const res = await service.globalSearch.submitSearch({
        title,
        query: aiKeywordFilter,
        filter: payload,
        options: {
          trend_frequency: frequency,
        },
      });

      setSearchStoreState((prev) => {
        return {
          ...prev,
          alias: res.alias,
          searchResult: res,
        };
      });

      // TODO: 현재 Search의 상태가 바로 변경되지 않아 명시적으로 업데이트
      updateOriginalFilterQueryFormats(filterQueryFormats);
      updateOriginalDateRange(dateRange);
      updateOriginalKeywordFilter(keywordFilter);
      updateOriginalAIKeywordFilter(aiKeywordFilter);
      updateFrequency(frequency);
      // refetch search result
      queryClient.invalidateQueries({ queryKey: queryKeys.search.SEARCH_HISTORY_LIST });
    } catch (e) {
      console.error(e);
    }
  };

  const onReSearchClick = () => {
    updateSearchResult();
    track(AMP_KEYS.SEARCH_SUBMIT, {
      from: 'detail',
    });
  };

  useTrackModal({
    domain: 'global-search',
    alias: searchResult.alias || '',
  });

  const pollingCount = usePolling({
    enabled: searchResult.semantic_result_status !== SEARCH_RESULT_STATUS.DONE,
    fetcher: () => service.globalSearch.fetchSearchResult(searchResult.alias),
    getCondition: (data) =>
      data.semantic_result_status === SEARCH_RESULT_STATUS.DONE,
    interval: 5000,
    onFetch: (data) => {
      setSearchStoreState((prev) => ({
        ...prev,
        searchResult: data,
      }));
    },
    onDone: (data) => {
      setSearchStoreState((prev) => ({
        ...prev,
        searchResult: data,
      }));
    },
  });

  const onNotifyClick = () => {
    addToNotifyList(searchResult.alias);
    addToast('We’ll notify you know once the analysis is ready.', 'success');
  };

  const onShareClick = () => {
    if (searchResult.alias) {
      track(AMP_KEYS.SEARCH_SHARE_CLICK);
      copyLink(searchResult.alias);
    }
  };

  const hasTaxonomyFilter = filterQueryFormats.find(
    (f) => f.columnKey === FEEDBACK_COLUMNS.TAXONOMY_TOPIC,
  );

  const onSaveClick = async () => {
    if (!currWorkspace?.saved_taxonomy_id)
      throw new Error('Saved topic taxonomy alias is not found');

    const taxonomy = await service.taxonomy.fetchTaxonomyItem(
      currWorkspace.saved_taxonomy_id,
    );

    if (taxonomy.topic_count >= MAX_SAVED_TOPIC_COUNT) {
      setPopup({
        type: 'confirm',
        popupTheme: 'positive',
        title: `You cannot save more than ${MAX_SAVED_TOPIC_COUNT} items in Saved Topics.`,
        message:
          'Please delete some of your previously saved topics and try again.',
        confirmText: 'Go to saved topics',
        onConfirm: async () => {
          await closeModal();
          wr.push(`/taxonomy/${currWorkspace?.saved_taxonomy_id}/feedback`);
        },
      });
      return;
    }
    createModalStatus.open();
  };

  return (
    <Style>
      <Flex j="space-between" a="flex-start" className="detail-sticky-header">
        <VFlex f={1}>
          <SearchTitle />
        </VFlex>
        <Flex g={12} a="center">
          <Button
            variant="outlined"
            onClick={onSaveClick}
            leftIcon={<BookmarkIcon />}
            disabled={filterChanged || !!hasTaxonomyFilter}
            sizeType="m"
            tooltip={
              filterChanged
                ? 'Please re-search to save'
                : hasTaxonomyFilter
                  ? "You can't create a new topic while the 'Taxonomy' filter is applied."
                  : null
            }
          >
            Save as topic
          </Button>
          <Button variant="outlined" onClick={onShareClick} sizeType="m">
            Share
          </Button>
          <IconButton
            icon={<CloseOutlined />}
            sizeType="m"
            onClick={closeModal}
            tooltip="Close"
          />
        </Flex>
      </Flex>
      <Flex className="filter-area" a="flex-end" j="space-between" g={20}>
        <VFlex g={10} f={1}>
          <FilterRow>
            <FilterLabel>Prompt</FilterLabel>
            <SingleKeywordFilter
              value={aiKeywordFilter}
              onChange={updateAiKeywordFilter}
            />
          </FilterRow>
          <FilterRow>
            <FilterLabel>Keyword</FilterLabel>
            <InlineKeywordFilterV2
              name="search-detail-filter"
              keywordFilter={keywordFilter}
              onChange={updateKeywordFilter}
              allowSpace
            />
          </FilterRow>
          <FilterRow>
            <FilterLabel>Filter</FilterLabel>
            <StructuredFilter
              values={filterQueryFormats}
              onChange={updateDataFilter}
              clearFilter={clearFilter}
            />
          </FilterRow>
          <FilterRow>
            <FilterLabel>Date</FilterLabel>
            <SearchDateFilter
              dateRange={dateRange}
              onChange={updateDateFilter}
            />
          </FilterRow>
        </VFlex>

        <Button
          variant="outlined"
          sizeType="m"
          disabled={!filterChanged || isFilterEmpty}
          onClick={onReSearchClick}
        >
          Re-search
        </Button>
      </Flex>

      <Stage className="flex flex-col flex-1">
        <Stage.Scene
          className="pb-8"
          status={
            searchResult.semantic_result_status === SEARCH_RESULT_STATUS.DONE
          }
        >
          <Space h={16} />
          <SearchTab
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <Space h={24} />
          <VFlex padding="0px 32px" style={{ flex: 1 }}>
            <SearchTabRender selectedTab={selectedTab} />
            <Space h={24} />
          </VFlex>
          <AskSynclyFloat
            onAskSyncly={(prompt) => {
              const fetchSimpleFeedbacks = async (
                sortStatus: NotNullableSortStatusT,
                pageParam?: null | string,
              ) => {
                const res = await service.globalSearch.fetchSearchFeedbackList(
                  searchResult.alias,
                  {
                    query: {
                      cursor: pageParam,
                      sortby: sortStatus.sortBy,
                      order: sortStatus.orderStatus,
                    },
                    payload: {},
                  },
                );
                const PAGE_SIZE = 50;
                return {
                  list: res.feedbacks,
                  count: res.count,
                  paginate_token:
                    res.feedbacks[PAGE_SIZE - 1]?.paginate_token || null,
                };
              };

              const submit = () => {
                track(AMP_KEYS.SEARCH_INLINE_ASK_SYNCLY_SUBMIT);
                setNewModal<FeedbackBlockListModalOptions>({
                  alias: searchResult.alias,
                  type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
                  idx: 0,
                  options: {
                    queryKeys: [
                      ...queryKeys.search.SEARCH_DETAIL,
                      ...queryKeys.search.SEARCH_RESULT_FEEDBACK_LIST,
                      searchResult.alias,
                    ],
                    title: `${searchResult.title} feedback`,
                    domain: 'global-search',
                    fetcher: fetchSimpleFeedbacks,
                    socketInfo: {
                      domain: GPT_DOMAIN.GLOBAL_SEARCH,
                      search: {
                        alias: searchResult.alias,
                      },
                      initialState: isEmptyString(prompt)
                        ? CollapseState.COLLAPSED
                        : CollapseState.EXPANDED,
                      initialPrompt: isEmptyString(prompt) ? undefined : prompt,
                    },
                  },
                });
              };
              submit();
            }}
            style={{ position: 'sticky', bottom: 32, zIndex: 10 }}
          />
        </Stage.Scene>
        <Stage.Scene
          status={
            searchResult.semantic_result_status !== SEARCH_RESULT_STATUS.DONE
          }
          className="p-8"
        >
          <ContentBox
            g={24}
            padding="70px 0px"
            a="center"
            j="center"
            d="column"
          >
            <VFlex g={12} j="center" a="center">
              <Flex a="center" g={10}>
                <Spinner />
                <Typo type="label1">Analyzing your data</Typo>
              </Flex>
              <Typo type="label3" className="text-gray-600">
                We will notify you once the analysis is ready!
              </Typo>
            </VFlex>
            <Button variant="outlined" onClick={onNotifyClick} sizeType="m">
              Notify me
            </Button>
          </ContentBox>
          <Space h={24} />
          <ContentBox g={24} f={1} padding="24px">
            <SearchFeedbackList
              alias={searchResult.alias}
              style={{ flex: 1 }}
              hideButton
              refetchKey={pollingCount.toString()}
              useSkeleton
            />
          </ContentBox>
        </Stage.Scene>
      </Stage>

      <Modal
        status={createModalStatus.status}
        closeModal={createModalStatus.close}
      >
        <SaveAsTopicModal
          aiKeywordFilter={aiKeywordFilter}
          keywordFilter={keywordFilter}
          dateFilter={dateRange}
          dataFilter={filterQueryFormats}
          searchResult={searchResult}
        />
      </Modal>
    </Style>
  );
};

const Style = styled(ScrollVFlex)`
  position: relative;
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray100};
  overflow: auto;
  flex: 1;
  height: 100%;
  ${hiddenScrollbar};
  .detail-sticky-header {
    padding: 32px 32px 16px;
    width: 100%;
    top: 0;
    z-index: 1;
    background-color: ${(props) => props.theme.colors.gray150};
    position: sticky;
    top: 0;
  }
  .filter-area {
    width: 100%;
    padding: 16px 32px 32px;
    background-color: ${(props) => props.theme.colors.gray150};
  }
  .whole-line {
    grid-column: 1 / 3;
  }
`;

const FilterRow = styled(Flex).attrs({
  a: 'center',
  g: 16,
})`
  width: 100%;
`;

const FilterLabel = styled(Typo).attrs({
  type: 'label2',
  className: 'text-gray-600',
})`
  width: 120px;
`;

export default SearchDetail;
